import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@toki/auth';
import { Topbar } from '../components/Topbar';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import { getParametros, saveParametros, type ParamItem, type ParamsResponse } from '../services/api';
import styles from './Parametros.module.css';

/**
 * PARÁMETROS (paso 101): lo operativo que se cambia sin desplegar. Portado de
 * Toki (`screens/Parametros.tsx`), sin lo municipal.
 *
 * El catálogo lo manda el backend (`app/core/app_settings.py`): familias,
 * grupos, etiquetas, ayudas, rangos y valores por defecto. Aquí no se escribe
 * ninguna lista de parámetros — en Toki una lista a mano en el front dejó ocho
 * sin pintar (`af93195b`).
 *
 * Se guarda en LOTE: solo lo cambiado, en un PUT, y el servidor rechaza el
 * lote entero si uno no vale. Lo guardado vale en la siguiente petición.
 * Solo admin (`system:manage_settings`).
 */
export function ParametrosScreen() {
  const { provider } = useAuth();
  const mc = getModuleColor('parametros');
  const ModuleIcon = getModuleIcon('parametros');
  const [data, setData] = useState<ParamsResponse | null>(null);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback((d: ParamsResponse) => {
    setData(d);
    setVals(Object.fromEntries(d.groups.flatMap((g) => g.items.map((i) => [i.key, i.value]))));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const t = await provider.getAccessToken();
        if (t) cargar(await getParametros(t));
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [provider, cargar]);

  const original = useMemo(
    () => Object.fromEntries((data?.groups ?? []).flatMap((g) => g.items.map((i) => [i.key, i.value]))),
    [data],
  );
  const cambios = Object.keys(vals).filter((k) => vals[k]!.trim() !== (original[k] ?? '').trim());

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setOk(null);
    try {
      const t = await provider.getAccessToken();
      if (!t) throw new Error('Sesión caducada');
      cargar(await saveParametros(t, Object.fromEntries(cambios.map((k) => [k, vals[k]!.trim()]))));
      setOk(cambios.length === 1 ? 'Guardado. Se aplica desde ya.' : `${cambios.length} cambios guardados. Se aplican desde ya.`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  if (!data) {
    return <div className={styles.loading}>{error ?? 'Cargando…'}</div>;
  }

  return (
    <div className={styles.page}>
      <Topbar
        title="Parámetros"
        subtitle="Lo que se ajusta sin desplegar: se aplica en cuanto se guarda"
        moduleIcon={<ModuleIcon weight="duotone" size={18} color={mc.accent} />}
        moduleColor={mc.accent}
        moduleBg={mc.bg}
      />

      <div className={styles.body}>
        <nav className={styles.indice} aria-label="Grupos de parámetros">
          {data.families.map((f) => (
            <div key={f.key} className={styles.familia}>
              <span className={styles.familiaLabel}>{f.label}</span>
              {data.groups.filter((g) => g.family === f.key).map((g) => (
                <a key={g.key} href={`#grupo-${g.key}`} className={styles.indiceItem}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`grupo-${g.key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}>
                  {g.label}
                </a>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.contenido}>
          <div className={styles.barra}>
            {/* El error va AQUÍ, en la barra fija, y no encima del contenido: el
                campo malo suele estar más abajo y el aviso quedaba fuera de la
                vista — se veía el «Guardar» sin efecto y nada más. */}
            <span className={`${styles.barraTxt} ${error ? styles.barraError : ''}`} role={error ? 'alert' : undefined}>
              {error
                ?? (cambios.length === 0
                  ? (ok ?? 'Sin cambios')
                  : cambios.length === 1 ? '1 cambio sin guardar' : `${cambios.length} cambios sin guardar`)}
            </span>
            {cambios.length > 0 && (
              <button type="button" className={styles.btnGhost} onClick={() => setVals(original)} disabled={guardando}>
                Descartar
              </button>
            )}
            <button type="button" className={styles.btnPrimary} onClick={guardar} disabled={guardando || cambios.length === 0}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>

          {data.families.map((f) => (
            <section key={f.key} className={styles.familiaSec}>
              <h2 className={styles.familiaTitulo}>{f.label}</h2>
              {data.groups.filter((g) => g.family === f.key).map((g) => (
                <div key={g.key} id={`grupo-${g.key}`} className={styles.grupo}>
                  <h3 className={styles.grupoTitulo}>{g.label}</h3>
                  {g.items.map((i) => (
                    <Fila
                      key={i.key}
                      item={i}
                      valor={vals[i.key] ?? i.value}
                      modificado={(vals[i.key] ?? '').trim() !== i.value.trim()}
                      onChange={(v) => { setOk(null); setError(null); setVals((x) => ({ ...x, [i.key]: v })); }}
                    />
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Coma decimal para la vista: es como se lee un número en español. El
 *  servidor acepta las dos. */
function bonito(v: string, tipo: ParamItem['type']): string {
  return tipo === 'float' ? v.replace('.', ',') : v;
}

function Fila({
  item, valor, modificado, onChange,
}: { item: ParamItem; valor: string; modificado: boolean; onChange: (v: string) => void }) {
  const enDefecto = valor.trim().replace(',', '.') === item.default.trim();
  const id = `param-${item.key}`;
  const rango = item.min != null && item.max != null
    ? `Entre ${bonito(String(item.min), item.type)} y ${bonito(String(item.max), item.type)}`
    : null;
  return (
    <div className={styles.fila}>
      <div className={styles.filaIzq}>
        <label htmlFor={id} className={styles.etiqueta}>
          {item.label}
          {modificado && <span className={styles.modificado}>modificado</span>}
        </label>
        <p className={styles.ayuda}>{item.help}</p>
      </div>
      <div className={styles.filaDer}>
        {item.type === 'bool' ? (
          <label className={styles.bool}>
            <input id={id} type="checkbox" checked={valor === 'true'}
              onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
            <span>{valor === 'true' ? 'Activado' : 'Desactivado'}</span>
          </label>
        ) : (
          <div className={styles.campo}>
            <input
              id={id}
              className={styles.input}
              type="text"
              inputMode={item.type === 'int' ? 'numeric' : 'decimal'}
              value={bonito(valor, item.type)}
              onChange={(e) => onChange(e.target.value.replace(',', '.'))}
              aria-describedby={rango ? `${id}-rango` : undefined}
            />
            {item.unit && <span className={styles.unidad}>{item.unit}</span>}
          </div>
        )}
        {rango && <span id={`${id}-rango`} className={styles.rango}>{rango}</span>}
        {!enDefecto && (
          <button type="button" className={styles.restablecer} onClick={() => onChange(item.default)}>
            Restablecer ({bonito(item.default, item.type)})
          </button>
        )}
      </div>
    </div>
  );
}
